import { Injectable, inject } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { OfflineService } from '../../services/pwa/offline.service';
import { SyncQueueService } from '../../services/pwa/sync-queue.service';
import { environment } from '../../../environments/environment';

/**
 * Interceptor HTTP para gerenciar requisições offline
 *
 * Funcionalidades:
 * - Detecta quando está offline
 * - Enfileira mutações (POST/PUT/DELETE) para sincronização posterior
 * - Permite GET continuar (usa cache se disponível)
 * - Sincroniza automaticamente quando volta online
 */
@Injectable()
export class OfflineInterceptor implements HttpInterceptor {
    private readonly offlineService = inject(OfflineService);
    private readonly syncQueueService = inject(SyncQueueService);

    // Set para rastrear requisições já processadas (evitar duplicação)
    private readonly processedRequests = new Set<string>();

    // URLs que não devem ser enfileiradas (ex: login, refresh token)
    private readonly skipQueueUrls = [
        '/api/auth/login',
        '/api/auth/refresh',
        '/api/auth/logout',
        'auth/login',
        'auth/refresh',
        'auth/logout',
    ];

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Verificar se está offline
        const isOffline = this.offlineService.isOffline();

        // Se está online, processar normalmente
        if (!isOffline) {
            return next.handle(request);
        }

        // Se está offline, verificar tipo de requisição
        const method = request.method.toUpperCase();
        const url = this.normalizeUrl(request.url);

        // GET: Tentar usar cache ou falhar silenciosamente
        if (method === 'GET') {
            return next.handle(request).pipe(
                catchError(() => {
                    // Em offline, GET falha silenciosamente (cache será usado se disponível)
                    return throwError(() => new Error('Offline: GET request failed'));
                })
            );
        }

        // POST/PUT/DELETE: Enfileirar para sincronização
        if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
            // Verificar se deve pular enfileiramento
            if (this.shouldSkipQueue(url)) {
                return next.handle(request);
            }

            // Criar chave única para esta requisição (evitar duplicação)
            const requestKey = `${method}:${url}:${JSON.stringify(request.body)}`;

            // Verificar se já foi processada
            if (this.processedRequests.has(requestKey)) {
                // Retornar resposta offline já processada
                const responseBody = {
                    success: true,
                    message: 'Requisição já enfileirada',
                    offline: true,
                    data: request.body,
                };
                return of(new HttpResponse({
                    status: 202,
                    statusText: 'Accepted',
                    body: responseBody,
                }));
            }

            // Marcar como processada
            this.processedRequests.add(requestKey);

            // Limpar após 5 segundos (evitar acúmulo de memória)
            setTimeout(() => {
                this.processedRequests.delete(requestKey);
            }, 5000);

            // Enfileirar requisição
            const priority = this.determinePriority(method, url);
            const itemId = this.syncQueueService.adicionarItem(
                url,
                method as 'POST' | 'PUT' | 'DELETE',
                request.body,
                priority,
                {
                    headers: this.extractHeaders(request),
                    description: this.generateDescription(method, url),
                }
            );

            // Retornar sucesso imediato (dados serão sincronizados depois)
            // Isso permite que o usuário continue trabalhando offline
            // Retornar HttpResponse para que o HttpClient processe corretamente
            const responseBody = {
                success: true,
                message: 'Requisição enfileirada para sincronização',
                syncItemId: itemId,
                offline: true,
                data: request.body, // Incluir dados originais para compatibilidade
            };

            // Retornar HttpResponse - HttpClient extrai body automaticamente
            return of(new HttpResponse({
                status: 202,
                statusText: 'Accepted',
                body: responseBody,
            }));
        }

        // Outros métodos: processar normalmente (pode falhar)
        return next.handle(request);
    }

    /**
     * Normaliza URL removendo baseUrl se presente
     */
    private normalizeUrl(url: string): string {
        // Remover protocolo e domínio se presente
        // Verificar se tem apiUrl no environment (pode não existir em prod)
        const baseUrl = (environment as any).apiUrl || '/api';
        if (url.startsWith(baseUrl)) {
            return url.substring(baseUrl.length);
        }
        if (url.startsWith('/api')) {
            return url.substring(4);
        }
        return url.startsWith('/') ? url : `/${url}`;
    }

    /**
     * Verifica se URL deve pular enfileiramento
     */
    private shouldSkipQueue(url: string): boolean {
        return this.skipQueueUrls.some(skipUrl => url.includes(skipUrl));
    }

    /**
     * Determina prioridade baseado no método e endpoint
     */
    private determinePriority(method: string, url: string): 'high' | 'medium' | 'low' {
        // Alta prioridade: ações críticas de ronda
        if (url.includes('/patrols/') && (url.includes('/start') || url.includes('/complete') || url.includes('/checkpoints'))) {
            return 'high';
        }

        // Alta prioridade: início/fim de turno
        if (url.includes('/shifts/') && (url.includes('/start') || url.includes('/end'))) {
            return 'high';
        }

        // Alta prioridade: eventos de pânico
        if (url.includes('/panic')) {
            return 'high';
        }

        // Média prioridade: outras mutações
        if (['POST', 'PUT', 'PATCH'].includes(method)) {
            return 'medium';
        }

        // Baixa prioridade: DELETE
        return 'low';
    }

    /**
     * Extrai headers relevantes do request
     */
    private extractHeaders(request: HttpRequest<any>): Record<string, string> {
        const headers: Record<string, string> = {};
        request.headers.keys().forEach(key => {
            const value = request.headers.get(key);
            if (value) {
                headers[key] = value;
            }
        });
        return headers;
    }

    /**
     * Gera descrição amigável para a requisição
     */
    private generateDescription(method: string, url: string): string {
        const methodName = method === 'POST' ? 'Criar' : method === 'PUT' ? 'Atualizar' : method === 'DELETE' ? 'Excluir' : 'Modificar';

        // Extrair nome da entidade da URL
        const parts = url.split('/').filter(p => p);
        const entity = parts[parts.length - 1] || parts[parts.length - 2] || 'item';

        return `${methodName} ${entity}`;
    }
}
